import React, { useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  Typography,
  useMediaQuery
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Forum, Language, SportsEsports, Verified } from '@mui/icons-material'
import { useLocation, useNavigate } from 'react-router-dom'
import PartnershipsSection from '../components/home/PartnershipsSection'
import PackagesSection from '../components/packages/PackagesSection'
import { useAuth } from '../contexts/AuthContext'

const scrollToId = (id: string) => {
  const el = document.getElementById(id)
  el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const Home: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated } = useAuth()

  const bannerUrl = isMobile ? '/banner-mobile.png' : '/banner.png'

  useEffect(() => {
    const sp = new URLSearchParams(location.search)
    const scroll = sp.get('scroll')
    if (!scroll) return

    const timer = window.setTimeout(() => scrollToId(scroll), 50)
    sp.delete('scroll')
    const nextSearch = sp.toString()
    navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true })

    return () => window.clearTimeout(timer)
  }, [location.pathname, location.search, navigate])

  return (
    <Box>
      <Box sx={{ pt: { xs: 2, md: 3 } }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              position: 'relative',
              borderRadius: { xs: 3, md: 4 },
              overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.10)',
              boxShadow: '0 24px 80px rgba(0,0,0,0.45)',
              backgroundColor: 'rgba(255,255,255,0.03)',
              aspectRatio: { xs: '1 / 1', md: '3 / 2' }
            }}
          >
            <Box
              component="img"
              src={bannerUrl}
              alt="Palworld banner"
              sx={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: -2.75, md: -3.25 }, pb: { xs: 2, md: 3 } }}>
            <Button
              variant="contained"
              color="info"
              size="large"
              onClick={() => scrollToId('pacotes')}
              sx={{
                fontWeight: 900,
                px: 4,
                py: 1.3,
                borderRadius: 999,
                boxShadow: '0 18px 45px rgba(0,0,0,0.45)'
              }}
            >
              Explorar Pacotes
            </Button>
          </Box>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={3} sx={{ alignItems: 'center' }}>
            <Grid item xs={12} md={8}>
              <Stack spacing={1.25}>
                <Typography variant="h3" sx={{ fontWeight: 900, lineHeight: 1.06 }}>
                  Palworld do seu jeito, com a estabilidade da{' '}
                  <span style={{ color: '#00e676' }}>Warzone Servers</span>
                </Typography>
                <Typography sx={{ color: 'text.secondary', maxWidth: 820 }}>
                  Escolha um pacote e tenha um servidor pronto para jogar com amigos, comunidade ou eventos.
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction={{ xs: 'column', sm: 'row', md: 'column' }} spacing={1.25} sx={{ justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={() => navigate(isAuthenticated ? '/cliente' : '/?login=1')}
                  sx={{ fontWeight: 900 }}
                >
                  Entrar na área do cliente
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <PackagesSection />

      <PartnershipsSection />

      <Box id="como-funciona" sx={{ py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Stack spacing={1} sx={{ mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: 900 }}>
              Como funciona
            </Typography>
            <Typography sx={{ color: 'text.secondary', maxWidth: 780 }}>
              Guia rápido em 4 passos para entrar no servidor, vincular seu Discord e acessar o site com stats e loja.
            </Typography>
          </Stack>

          <Grid container spacing={2.5}>
            {[
              {
                title: '1º passo — Acesse o servidor',
                desc: 'Entre no BR_Warzone_Palworld e comece sua aventura.',
                icon: <SportsEsports />
              },
              {
                title: '2º passo — Vincule no Discord',
                desc: 'No #link-new-players, use !link palworld seu@email.com para receber o código.',
                icon: <Forum />
              },
              {
                title: '3º passo — Confirme no jogo',
                desc: 'Digite link 0000 no chat e finalize a validação.',
                icon: <Verified />
              },
              {
                title: '4º passo — Acesse o site',
                desc: 'Estatísticas, eventos, histórico e loja em um só lugar.',
                icon: <Language />
              }
            ].map((it) => (
              <Grid key={it.title} item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        width: 46,
                        height: 46,
                        borderRadius: 3,
                        display: 'grid',
                        placeItems: 'center',
                        mb: 2,
                        background:
                          'linear-gradient(135deg, rgba(0,230,118,0.26), rgba(0,217,255,0.20), rgba(255,109,0,0.18))',
                        border: '1px solid rgba(255,255,255,0.10)'
                      }}
                    >
                      {it.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.75 }}>
                      {it.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {it.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-start' }, mt: 3 }}>
            <Button
              variant="contained"
              color="info"
              size="large"
              onClick={() => navigate('/como-funciona')}
              sx={{ fontWeight: 900, borderRadius: 999, px: 4 }}
              fullWidth={isMobile}
            >
              Ver guia completo
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  )
}

export default Home
